import { useEffect, useRef, useState } from 'react';
import { debounce } from 'ts-debounce';
import BridgeLink from '@/components/base/BridgeLink';
import CloseButton from '@/components/base/CloseButton';
import styles from './AlertDialog.module.scss';

type AlertDialogProps = {
  bgColor?: string
  label?: string
  link?: string
  message: string
  onClose?: () => void
  textColor?: string
}
export default function AlertDialog({
  bgColor,
  label,
  link,
  message,
  onClose,
  textColor,
}: AlertDialogProps) {
  const [ isClosing, setIsClosing ] = useState(false);
  const alertRef = useRef<HTMLDivElement|null>(null);

  const handleClose = (): void => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose?.();
    }, 150);
  };

  const onResize = (heightOverride?: number) => {
    const bannerHeight = typeof heightOverride === 'number' ?
      heightOverride :
      alertRef.current?.offsetHeight;

    document.body.style.setProperty('--pbsk-message-banner-height', `${bannerHeight}px`);
  };

  useEffect(() => {
    const debouncedOnResize = debounce((_ev)=> onResize(), 200);
    setTimeout(onResize, 1);
    window.addEventListener('resize', debouncedOnResize);

    return () => {
      window.removeEventListener('resize', debouncedOnResize);
      onResize(0);
    };
  }, []);

  const getMessage = () => {
    if (link) {
      const url = new URL(link);
      const hostname = url.hostname;
      if ( (/^(www\.)?pbskids\.org$/i).test(hostname)) {
        return <a
          className={styles.noBridgeLink}
          href={ link }>
          { message } &rsaquo;
        </a>;
      } else {
        return <BridgeLink href={link} title=''>{ message }&nbsp;&nbsp;&rsaquo;</BridgeLink>;
      }
    }
    return message;
  };

  return <>
    { (bgColor || textColor) &&
      <style jsx global>{`
          :root {
            ${bgColor ? ('--pbsk-component-message-banner-background-color: ' + bgColor + ';') : '' }
            ${textColor ? ('--pbsk-component-message-banner-text-color: ' + textColor + ';') : '' }
          }
      `}</style>
    }
    <div
      ref={alertRef}
      aria-label={label}
      className={`${styles.wrapper} ${isClosing ? styles.slideOut : ''}`}
      role='alertdialog'>
      <CloseButton
        className={styles.alertCloseButton}
        handleClick={handleClose} />
      <div className={styles.alertContent}>
        { getMessage() }
      </div>
    </div>
  </>;
}
